import React, { useContext, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useShipmentLabels } from '@/components/LabelPreviewModal/useShipmentLabels';
import RetryDeliveryModal, {
  RetryReasonCode,
} from '@/components/RetryDeliveryModal';
import { canRetryDelivery } from '@/config/ops-permissions';
import AuthContext from '@/contexts/AuthContext';
import { toast } from '@/design-system/toaster';
import { shipmentsAPI } from '@/services/api/shipments';
import { Alert } from '@/design-system/alert';
import { Button } from '@/design-system/button';
import { TableData, type TableDataColumn } from '@/design-system/table-data';

import { warehouseApi } from '../../api/warehouse.api';
import { DisabledReason } from '../../components/dialogs';
import OutboundRequestEditModal from '../../components/OutboundRequestEditModal';
import { OutboundTransferTag } from '../../components/orderType';
import {
  codeCls,
  DetailHeader,
  DetailSkeleton,
  KV,
  KVGrid,
  LoadError,
  linkCls,
  MappedBadge,
  metaCls,
  nameCls,
  numCls,
  Section,
  SourceTag,
  StatusBadge,
} from '../../components/ui';
import { HistorySection, outboundOrderHistory } from '../../components/history';
import { useOutboundActions } from '../../components/useOutboundActions';
import { useOutboundShippingLabels } from '../../components/useOutboundShippingLabels';
import { useUndoShipping } from '../../components/useUndoShipping';
import { hasThirdPartyShippingLabel } from '../../print/shippingLabels';
import { fmtDate, fmtDateTime, fmtDistance, fmtQty } from '../../format';
import { useLoad } from '../../hooks/useLoad';
import { goodsIssueUrl, shipmentsUrl } from '../../links';
import {
  canEditOutbound,
  canUndoShipping,
  carrierLabel,
  FULFILLMENT_STATUS,
  isPickingStage,
  metaOf,
  methodLabel,
  canCancelAfterShip,
  OUTBOUND_CANCELLABLE,
  OUTBOUND_STATUS,
  OUTBOUND_TYPE_META,
  serviceLabel,
  SOURCE_META,
} from '../../status';
import type { OutboundOrder, OutboundOrderLine } from '../../types';
import { useWarehouse } from '../../WarehouseContext';
import { WAREHOUSE_PATHS } from '../../paths';

const OutboundRequestDetailPage: React.FC = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { refreshToken } = useWarehouse();
  const detail = useLoad(
    (signal) => warehouseApi.getOutboundOrder(id, { signal }),
    [id, refreshToken]
  );
  const actions = useOutboundActions(detail.reload);
  // "Batal submit shipping": back to Ready to pick up while still at the hub.
  const undoShip = useUndoShipping(
    () => detail.reload(),
    'outbound-undo-ship-dialog'
  );
  // Same shipping label as Shipments/Packages, so ops don't switch menus.
  const labels = useShipmentLabels();
  // A 3PL order has no shipment: its label is built from the order instead.
  const outboundLabels = useOutboundShippingLabels(labels.open);
  // Ops retry of the FAILED delivery behind the fulfillment shipment — same
  // email allowlist as the delivery/shipment pages (fail closed).
  // useContext, not useAuth: fail closed (deny) outside a provider.
  const user = useContext(AuthContext)?.user;
  const [retryOpen, setRetryOpen] = useState(false);
  const [retryLoading, setRetryLoading] = useState(false);
  const [editing, setEditing] = useState<OutboundOrder | null>(null);

  if (detail.loading && !detail.data) return <DetailSkeleton />;
  if (detail.error || !detail.data) {
    return (
      <div className="space-y-5 p-6">
        <DetailHeader
          backTo={WAREHOUSE_PATHS.outboundRequest}
          backLabel="Outbound request"
          title={id}
        />
        <LoadError
          message={detail.error?.message ?? 'Outbound request tidak ditemukan'}
          onRetry={detail.reload}
        />
      </div>
    );
  }

  const { order: o, lines, events } = detail.data;
  const gi = o.goodsIssue;
  const giPath = gi ? goodsIssueUrl(gi) : null;
  const f = o.fulfillment;
  const shipped = ['SHIPPED', 'IN_DELIVERY', 'DELIVERED', 'FAILED'].includes(
    o.status
  );
  const canUndoShip =
    !!gi &&
    canUndoShipping({
      goodsIssueStatus: gi.status,
      orderStatus: o.status,
      fulfillmentStatus: f?.status,
      carrier: f?.carrier,
    });
  const transfer = o.type === 'TRANSFER';
  const transferPO = transfer ? (o.transferPurchaseOrder ?? null) : null;
  /** SKUs the hub cannot allocate in full (picking order would 422). */
  const shortSkus =
    o.status === 'OPEN'
      ? lines.filter((l) => l.qtyAvailable < l.qty).map((l) => l.sku)
      : [];

  const productColumns: TableDataColumn<OutboundOrderLine>[] = [
    {
      key: 'sku',
      title: 'SKU',
      render: (_v, l) => <span className={codeCls}>{l.sku}</span>,
    },
    {
      key: 'itemName',
      title: 'Product name',
      render: (_v, l) => <span className={nameCls}>{l.itemName}</span>,
    },
    {
      key: 'qty',
      title: 'Qty order',
      align: 'right',
      render: (_v, l) => <span className={numCls}>{fmtQty(l.qty)}</span>,
    },
    {
      key: 'qtyReserved',
      title: 'Qty reserved',
      align: 'right',
      render: (_v, l) => (
        <span className={numCls}>{fmtQty(l.qtyReserved)}</span>
      ),
    },
    {
      key: 'qtyPicked',
      title: 'Qty picked',
      align: 'right',
      render: (_v, l) => <span className={numCls}>{fmtQty(l.qtyPicked)}</span>,
    },
    {
      key: 'uomName',
      title: 'UoM',
      render: (_v, l) => <span className={metaCls}>{l.uomName}</span>,
    },
    {
      key: 'qtyAvailable',
      title: 'Available di hub',
      align: 'right',
      render: (_v, l) => (
        <span
          className={`font-mono text-[12px] tabular-nums ${
            o.status === 'OPEN' && l.qtyAvailable < l.qty
              ? 'font-semibold text-(--state-warning-dark)'
              : 'text-text-sub-600'
          }`}
        >
          {fmtQty(l.qtyAvailable)}
        </span>
      ),
    },
  ];

  const manualCarrier = !!f?.carrier && f.carrier !== 'DASH';
  // Retry rides the Dash shipment behind the order; a third-party carrier
  // (LION_PARCEL) has none — its status stays the manual fulfillment flow.
  const canRetry =
    o.status === 'FAILED' &&
    !!f?.shipmentID &&
    !manualCarrier &&
    canRetryDelivery(user?.email);

  const handleRetryDelivery = async (
    reasonCode: RetryReasonCode,
    notes: string
  ) => {
    if (!f?.shipmentID) return;
    setRetryLoading(true);
    try {
      const res = await shipmentsAPI.retryDelivery(f.shipmentID, {
        reasonCode,
        notes,
      });
      toast.success('Delivery dikirim ulang', {
        description: `Delivery ${res.deliveryUid} kembali ke Allocating — shipment & outbound request ter-reset dalam request yang sama.`,
      });
      setRetryOpen(false);
      detail.reload();
    } catch (err) {
      toast.error('Gagal mengirim ulang delivery', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setRetryLoading(false);
    }
  };
  const shipmentValue =
    f?.waybill && f.shipmentID ? (
      <Link to={shipmentsUrl(f)} className={linkCls}>
        {f.waybill}
      </Link>
    ) : manualCarrier ? (
      // A third-party carrier: WMS issued the waybill, there is no Shipments row.
      <span className={codeCls}>{f?.waybill}</span>
    ) : shipped ? (
      <span className="text-[12px] text-text-sub-600">
        Integrasi Shipments nonaktif
      </span>
    ) : null;

  return (
    <div className="space-y-5 p-6">
      <DetailHeader
        backTo={WAREHOUSE_PATHS.outboundRequest}
        backLabel="Outbound request"
        meta={
          <>
            Dibuat {fmtDateTime(o.createdAt)} · via{' '}
            <span className="font-semibold text-text-sub-600">
              {SOURCE_META[o.source]?.label ?? o.source}
            </span>
            {o.upload && <span className="font-mono"> {o.upload.code}</span>} ·
            oleh{' '}
            <span className="font-semibold text-text-sub-600">
              {o.createdBy}
            </span>
          </>
        }
        title={o.code}
        badges={
          <>
            <StatusBadge meta={metaOf(OUTBOUND_STATUS, o.status)} />
            <SourceTag source={o.source} />
            <OutboundTransferTag order={o} />
          </>
        }
        actions={
          <>
            {shortSkus.length > 0 && (
              <DisabledReason testId="outbound-picking-reason">
                Stok belum cukup: {shortSkus.join(', ')}
              </DisabledReason>
            )}
            {o.status === 'OPEN' && (
              <Button
                loading={actions.isBusy(o.id)}
                disabled={shortSkus.length > 0}
                data-testid="outbound-create-picking"
                onClick={() => actions.requestPicking(o)}
                leftIcon={<i className="ri-file-list-3-line text-[14px]" />}
              >
                Create picking order
              </Button>
            )}
            {gi && isPickingStage(gi.status) && (
              <Button
                tone="neutral"
                style="stroke"
                onClick={() => navigate(`${WAREHOUSE_PATHS.picking}/${gi.id}`)}
                leftIcon={<i className="ri-box-3-line text-[14px]" />}
              >
                Buka picking
              </Button>
            )}
            {gi && ['READY_TO_PICKUP', 'SHIPPED'].includes(gi.status) && (
              <Button
                tone="neutral"
                style="stroke"
                onClick={() => navigate(`${WAREHOUSE_PATHS.shipping}/${gi.id}`)}
                leftIcon={<i className="ri-truck-line text-[14px]" />}
              >
                Buka shipping
              </Button>
            )}
            {transferPO && (
              <Button
                tone="neutral"
                style="stroke"
                onClick={() =>
                  navigate(`${WAREHOUSE_PATHS.purchaseOrder}/${transferPO.id}`)
                }
                leftIcon={<i className="ri-file-list-3-line text-[14px]" />}
              >
                Lihat PO transfer
              </Button>
            )}
            {/* The Dash shipment exists once the order ships (SHIPPED and
                later) and prints from it; a third-party carrier has none, so
                its label is built from the order (same content). */}
            {(f?.shipmentID || hasThirdPartyShippingLabel(f)) && (
              <Button
                tone="neutral"
                style="stroke"
                loading={labels.preparing || outboundLabels.preparing}
                data-testid="outbound-print-shipping-labels"
                onClick={() =>
                  f?.shipmentID
                    ? labels.print([f.shipmentID])
                    : outboundLabels.print([o.id])
                }
                leftIcon={<i className="ri-printer-line text-[14px]" />}
              >
                Print shipping labels
              </Button>
            )}
            {f?.shipmentID && (
              <Button
                tone="neutral"
                style="stroke"
                onClick={() => navigate(shipmentsUrl(f))}
                leftIcon={<i className="ri-external-link-line text-[14px]" />}
              >
                Lihat di Shipments
              </Button>
            )}
            {canRetry && (
              <Button
                tone="primary"
                style="stroke"
                data-testid="outbound-retry-delivery"
                onClick={() => setRetryOpen(true)}
                leftIcon={<i className="ri-restart-line text-[14px]" />}
              >
                Retry delivery
              </Button>
            )}
            {canEditOutbound(o) && (
              <Button
                tone="neutral"
                style="stroke"
                data-testid="outbound-edit"
                onClick={() => setEditing(o)}
                leftIcon={<i className="ri-edit-line text-[14px]" />}
              >
                Ubah
              </Button>
            )}
            {(OUTBOUND_CANCELLABLE.includes(o.status) ||
              canCancelAfterShip(o)) && (
              <Button
                tone="destructive"
                style="stroke"
                data-testid="outbound-cancel"
                onClick={() => actions.requestCancel(o)}
              >
                Cancel order
              </Button>
            )}
            {gi && canUndoShip && (
              <Button
                tone="destructive"
                style="stroke"
                data-testid="outbound-undo-ship"
                onClick={() =>
                  undoShip.request({
                    orderCode: o.code,
                    goodsIssueID: gi.id,
                    transfer,
                    carrier: f?.carrier,
                  })
                }
                leftIcon={<i className="ri-arrow-go-back-line text-[14px]" />}
              >
                Batal submit shipping
              </Button>
            )}
          </>
        }
      />

      {o.status === 'CANCELLED' && (
        <Alert
          status="warning"
          appearance="stroke"
          size="lg"
          title="Order dibatalkan"
        >
          {o.cancellationReason || '—'} · {fmtDateTime(o.cancelledAt)}
          {o.cancelledBy ? ` · ${o.cancelledBy}` : ''}
        </Alert>
      )}

      {!transfer && o.status !== 'CANCELLED' && o.destinationCityID === null && (
        // The city feeds the export and the list filter: say so up front,
        // not only in the Destination card.
        <Alert
          status="warning"
          appearance="stroke"
          size="lg"
          title="Kota tujuan belum dikenali"
          data-testid="outbound-city-alert"
        >
          Tidak ketemu dari teks alamat di master kota core — pilih kota lewat{' '}
          <strong>Ubah</strong> supaya ikut di export dan filter.
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <div className="flex flex-col gap-5 xl:col-span-2">
          <Section title="Detail order">
            <KVGrid
              items={[
                ['Request ID', <span className={codeCls}>{o.code}</span>],
                [
                  'External ID (client)',
                  <>
                    <span className={codeCls}>{o.externalID}</span>
                    {o.externalIDAuto && (
                      <span className={metaCls}> (otomatis)</span>
                    )}
                  </>,
                ],
                [
                  'Sumber',
                  <span className="inline-flex flex-wrap items-center gap-1.5">
                    <SourceTag source={o.source} />
                    {o.upload && (
                      <span className={metaCls}>
                        <span className="font-mono">{o.upload.code}</span> ·{' '}
                        {o.upload.fileName}
                      </span>
                    )}
                  </span>,
                ],
                [
                  'Picking order',
                  gi && giPath ? (
                    <Link to={giPath} className={linkCls}>
                      {gi.code}
                    </Link>
                  ) : null,
                ],
                [
                  'Nomor surat jalan',
                  gi?.suratJalanNo ? (
                    <span className={codeCls}>{gi.suratJalanNo}</span>
                  ) : null,
                ],
                ['Order date', fmtDateTime(o.orderDate)],
                ['Expected delivery', fmtDate(o.expectedDeliveryDate)],
                [
                  'Tipe',
                  OUTBOUND_TYPE_META[o.type ?? 'REGULAR']?.label ?? o.type,
                ],
                ...(transfer
                  ? ([
                      [
                        'Transfer warehouse',
                        o.hubTransfer ? (
                          <Link
                            to={`${WAREHOUSE_PATHS.hubTransfer}/${o.hubTransfer.id}`}
                            className={linkCls}
                            data-testid="outbound-hub-transfer-link"
                          >
                            {o.hubTransfer.code}
                          </Link>
                        ) : null,
                      ],
                      [
                        'PO transfer (hub tujuan)',
                        transferPO ? (
                          <Link
                            to={`${WAREHOUSE_PATHS.purchaseOrder}/${transferPO.id}`}
                            className={linkCls}
                          >
                            {transferPO.code}
                          </Link>
                        ) : null,
                      ],
                    ] as [string, React.ReactNode][])
                  : []),
                ['Method', methodLabel(o.method)],
                ['Service', serviceLabel(o.serviceType)],
                ['Catatan order', o.notes || null],
              ]}
            />
          </Section>
          <Section title="Products">
            <TableData size="sm" data={lines} columns={productColumns} />
          </Section>
          <HistorySection entries={outboundOrderHistory(events)} />
        </div>
        <div className="flex flex-col gap-5">
          {(shipmentValue || f?.status || o.courier) && (
            <Section title="Pengiriman">
              <KV
                label="Shipment (Waybill)"
                value={shipmentValue}
                sub={manualCarrier ? carrierLabel(f?.carrier) : undefined}
              />
              {f?.status && (
                <KV
                  label="Status shipment"
                  value={
                    <MappedBadge map={FULFILLMENT_STATUS} value={f.status} />
                  }
                />
              )}
              {f?.carrier === 'DASH' && (
                // The rider on the Dash shipment (read-time, so a reassignment
                // shows at once); a 3PL has no courier of ours.
                <KV
                  label="Kurir"
                  value={
                    o.courier ? (
                      <span data-testid="outbound-courier">{o.courier.name}</span>
                    ) : null
                  }
                  sub={
                    o.courier
                      ? `${o.courier.code} · ID ${o.courierID}`
                      : 'Belum ada kurir'
                  }
                />
              )}
              {o.courier?.phone && (
                <KV label="Telepon kurir" value={o.courier.phone} />
              )}
            </Section>
          )}
          <Section title="Origin">
            <KV
              label="Client"
              value={o.client?.name ?? `Client #${o.clientID}`}
            />
            <KV
              label="Hub"
              value={o.hub?.name ?? `Hub #${o.hubID}`}
              sub={o.hub?.code ?? undefined}
            />
          </Section>
          <Section title="Destination">
            {transfer && (
              <KV
                label="Hub tujuan"
                value={o.destinationHub?.name ?? null}
                sub={o.destinationHub?.code ?? undefined}
              />
            )}
            <KV label="Penerima" value={o.recipientName} />
            <KV label="Telepon" value={o.recipientPhone} />
            <KV label="Alamat" value={o.destinationAddress} />
            {!transfer && (
              <KV
                label="Kota"
                value={
                  o.destinationCity ? (
                    <span data-testid="outbound-city">{o.destinationCity}</span>
                  ) : (
                    <span
                      className="text-warning-base"
                      data-testid="outbound-city-missing"
                    >
                      <i className="ri-error-warning-line mr-1" />
                      Belum dikenali — pilih lewat Ubah
                    </span>
                  )
                }
              />
            )}
            <KV
              label="Koordinat"
              value={
                <span className={numCls}>
                  {o.destinationLatitude}, {o.destinationLongitude}
                </span>
              }
            />
            <KV
              label="Jarak dari hub"
              value={
                o.distance === null ? (
                  <span className="text-text-soft-400">
                    Dihitung saat dispatch
                  </span>
                ) : (
                  <span className={numCls} data-testid="outbound-distance">
                    {fmtDistance(o.distance)}
                  </span>
                )
              }
            />
            {o.destinationNotes && (
              <KV label="Catatan" value={o.destinationNotes} />
            )}
          </Section>
        </div>
      </div>
      {actions.dialogs}
      {undoShip.dialog}
      <OutboundRequestEditModal
        order={editing}
        onClose={() => setEditing(null)}
        onSaved={(saved) => {
          setEditing(null);
          toast.success('Outbound request diubah', {
            description:
              saved.fulfillment?.carrier === 'DASH' && saved.fulfillment.waybill
                ? `${saved.code} · shipment ${saved.fulfillment.waybill} ikut diubah`
                : saved.code,
          });
          detail.reload();
        }}
      />
      {labels.modal}
      <RetryDeliveryModal
        isOpen={retryOpen}
        onClose={() => setRetryOpen(false)}
        onConfirm={handleRetryDelivery}
        isLoading={retryLoading}
      />
    </div>
  );
};

export default OutboundRequestDetailPage;
